import React, { useState, useRef, useEffect } from 'react';
import { Wand2, ChevronDown } from 'lucide-react';
import { rewriteAsset } from '../utils/api';

const OPTIONS = [
  ['more_urgent', 'Make it more urgent'],
  ['more_premium', 'Make it more premium'],
  ['more_local', 'Make it more local'],
  ['shorter', 'Make it shorter'],
  ['friendlier', 'Make it friendlier'],
  ['add_financing', 'Add financing'],
  ['remove_discount', 'Remove discount'],
  ['add_warranty', 'Add warranty'],
  ['add_emergency', 'Add emergency service'],
  ['change_cta', 'Change the CTA'],
];

export const RewriteMenu = ({ assetId, onDone, onError }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [custom, setCustom] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const run = async (direction, customDirection) => {
    setOpen(false);
    setBusy(true);
    try {
      const { data } = await rewriteAsset(assetId, direction, customDirection);
      onDone?.(data);
    } catch (e) {
      onError?.(e.response?.data?.detail || 'Rewrite failed — try again.');
    } finally {
      setBusy(false);
      setCustom('');
    }
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        disabled={busy}
        data-testid="rewrite-menu-btn"
        className="inline-flex items-center gap-1 px-3 py-1.5 bg-lime/10 border border-lime/40 hover:bg-lime/20 text-lime text-xs font-bold rounded-lg transition-colors disabled:opacity-60"
      >
        {busy ? (
          <><span className="inline-block w-3 h-3 border-2 border-lime border-t-transparent rounded-full animate-spin" /> Rewriting…</>
        ) : (
          <><Wand2 size={13} /> Rewrite <ChevronDown size={12} /></>
        )}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-60 bg-card-bg border border-divider rounded-lg shadow-xl overflow-hidden" data-testid="rewrite-menu-dropdown">
          {OPTIONS.map(([id, label]) => (
            <button
              key={id}
              onClick={() => run(id)}
              data-testid={`rewrite-opt-${id}`}
              className="block w-full text-left px-4 py-2 text-sm text-light-gray hover:bg-black hover:text-white transition-colors"
            >
              {label}
            </button>
          ))}
          <div className="p-2 border-t border-divider">
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && custom.trim()) run('custom', custom.trim()); }}
              placeholder="Custom direction + Enter…"
              data-testid="rewrite-custom-input"
              className="w-full bg-black border border-divider rounded px-2.5 py-1.5 text-xs text-white focus:border-lime focus:outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const STATUS_STYLES = {
  ready: 'bg-gray-500/20 text-gray-300',
  personalized: 'bg-lime/15 text-lime',
  needs_review: 'bg-orange/15 text-orange',
  used: 'bg-blue-500/20 text-blue-400',
  approved: 'bg-lime/20 text-lime',
  launched: 'bg-orange/20 text-orange',
  tracking: 'bg-lime/20 text-lime',
};

export const STATUS_LABELS = {
  ready: 'Ready',
  personalized: 'Personalized',
  needs_review: 'Needs Review',
  used: 'Used',
  approved: 'Approved',
  launched: 'Launched',
  tracking: 'Tracking',
};
